/**
 * Fetches exchange rates from the fawazahmed0 currency API CDN.
 * Rates are keyed by lowercase source currency codes relative to the target currency.
 */

export type ExchangeRates = Record<string, number>;

async function fetchRatesFromUrl(url: string, targetCurrency: string): Promise<ExchangeRates | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const data = await response.json();
    const rates = data[targetCurrency.toLowerCase()];
    return rates && typeof rates === 'object' ? rates : null;
  } catch {
    return null;
  }
}

/** Fetch exchange rates for a specific date (YYYY-MM-DD). */
export async function fetchExchangeRatesForDate(
  date: string,
  targetCurrency: string
): Promise<ExchangeRates | null> {
  const target = targetCurrency.toLowerCase();
  const primaryUrl = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@${date}/v1/currencies/${target}.json`;
  const rates = await fetchRatesFromUrl(primaryUrl, targetCurrency);
  if (rates) return rates;

  const fallbackUrl = `https://${date}.currency-api.pages.dev/v1/currencies/${target}.json`;
  return fetchRatesFromUrl(fallbackUrl, targetCurrency);
}

/** Fetch today's exchange rates for converting into the target currency. */
export async function fetchLatestExchangeRates(targetCurrency: string): Promise<ExchangeRates | null> {
  const today = new Date().toISOString().slice(0, 10);
  return fetchExchangeRatesForDate(today, targetCurrency);
}

/**
 * Convert an amount from source to target currency.
 * API rates are FROM target TO source, so we divide by the rate.
 */
export function convertToTargetCurrency(
  amount: number,
  fromCurrency: string,
  targetCurrency: string,
  exchangeRates: ExchangeRates
): number | null {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (fromCurrency.toLowerCase() === targetCurrency.toLowerCase()) return amount;

  const fromKey = fromCurrency.toLowerCase();
  const rate = exchangeRates[fromKey];
  if (!rate || rate <= 0) return null;

  return amount / rate;
}
